import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import hiveService from '../services/hive';
import { useSettings } from '../context/SettingsContext';
import { useLists } from '../context/ListsContext';
import notificationsApi from '../services/notifications';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import { formatRelativeTime, formatAbsoluteTime } from '../utils/time';
import { buildContentPath, buildPostPath } from '../utils/postUrls';
import {
  NOTIFICATION_TYPE_LABELS,
  NOTIFICATION_TYPE_PAST_TENSE,
  ALL_NOTIFICATION_TYPES,
  getDefaultNotificationFilterTypes,
} from '../config/notificationTypes';
import './Notifications.css';

const NOTIFICATIONS_PAGE_SIZE = 200;
const MAX_DISPLAY_NOTIFICATIONS = 200;

function sortUnreadFirstByCreatedAt(items = []) {
  return [...items].sort((a, b) => {
    const readCmp = Number(Boolean(a?.is_read)) - Number(Boolean(b?.is_read));
    if (readCmp !== 0) return readCmp;
    return new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime();
  });
}

const TYPE_LABELS = {
  ...NOTIFICATION_TYPE_LABELS,
  notiflexion_list_article: 'List article',
};

const TYPE_PAST_TENSE = {
  ...NOTIFICATION_TYPE_PAST_TENSE,
  notiflexion_list_article: 'published',
};

function getAuthHeaders() {
  const token = localStorage.getItem('hive_web2_token');
  if (token) {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  const username = localStorage.getItem('hive_username');
  if (username) {
    return {
      'Content-Type': 'application/json',
      'X-Hive-Username': username,
    };
  }

  return null;
}

function getTxId(notification) {
  return notification?.tx_id || notification?.data?.tx_id || null;
}

function isReplyType(type) {
  return type === 'reply' || type === 'reply_mention';
}

function getPostAuthor(notification) {
  if (isReplyType(notification?.type)) {
    return notification?.data?.from || notification?.data?.author || null;
  }

  if (notification?.type === 'mention') {
    return notification?.data?.post_author || notification?.data?.author || notification?.data?.from || null;
  }

  return notification?.data?.post_author || notification?.data?.author || null;
}

function getPostPermlink(notification) {
  if (isReplyType(notification?.type)) {
    return notification?.data?.reply_permlink || notification?.data?.permlink || null;
  }

  if (notification?.type === 'mention') {
    return notification?.data?.post_permlink || notification?.data?.permlink || notification?.permlink || null;
  }

  return notification?.data?.post_permlink || notification?.permlink || null;
}

function getParentPermlinkLabel(notification) {
  const parentAuthor = notification?.data?.post_author || null;
  const parentPermlink = notification?.data?.post_permlink || null;
  if (!parentAuthor || !parentPermlink) return null;
  return `@${parentAuthor}/${parentPermlink}`;
}

function getReplyParentAuthor(notification) {
  return notification?.data?.post_author || null;
}

function getReplyParentPermlink(notification) {
  return notification?.data?.post_permlink || null;
}

function getReplyPermlinkLabel(notification) {
  if (!isReplyType(notification?.type)) return null;
  const replyAuthor = notification?.data?.from || notification?.data?.author || null;
  const replyPermlink = notification?.data?.reply_permlink || notification?.data?.permlink || null;
  if (!replyAuthor || !replyPermlink) return null;
  return `@${replyAuthor}/${replyPermlink}`;
}

function getPostUrl(notification) {
  const author = getPostAuthor(notification);
  const permlink = getPostPermlink(notification);
  if (!author || !permlink) return null;

  // Use available notification hints for an immediate best-effort route.
  const routeHints = {
    root_author: notification?.data?.root_author || notification?.data?.rootAuthor || null,
    parent_author: notification?.data?.parent_author || notification?.data?.parentAuthor || null,
  };

  return buildContentPath(author, permlink, routeHints);
}

function getNotificationSnippet(notification) {
  if (!notification || !notification.data) return null;
  // Always prefer title if present
  // Remove [img]...[/img] and [img ...] shortcodes, then return up to 140 visible chars
  const stripImgShortcode = (str) =>
    str
      .replace(/\[img\][^[]*?\[\/img\]/gi, '')
      .replace(/\[img[^\]]*\]/gi, '')
      .replace(/\s+/g, ' ');

  const getCleanSnippet = (str) => {
    if (!str) return null;
    const cleaned = stripImgShortcode(String(str)).trim();
    return cleaned.slice(0, 140);
  };

  const title = notification.data.title || notification.data.post_title || notification.data.postTitle;
  if (title) {
    return getCleanSnippet(title);
  }
  // Fallback to preview if no title
  const raw = notification.data.body_preview || notification.data.bodyPreview || null;
  if (!raw) return null;
  return getCleanSnippet(raw);
}

function getNotificationPermlink(notification) {
  const author = getPostAuthor(notification);
  const permlink = getPostPermlink(notification);
  if (!author || !permlink) return null;
  return `@${author}/${permlink}`;
}

function getMemoPostLink(memo) {
  if (typeof memo !== 'string') return null;

  const normalized = memo.trim();
  const match = normalized.match(/^@([a-z0-9.-]{3,32})\/([a-z0-9-]{3,255})$/);
  if (!match) return null;

  return {
    author: match[1],
    permlink: match[2],
  };
}

function renderNotificationMemo(memo) {
  if (!memo) return null;

  const tokens = String(memo).trim().split(/(\s+)/);
  const hasLinkToken = tokens.some((token) => getMemoPostLink(token));

  if (!hasLinkToken) {
    return <div className="notification-meta">Memo: {memo}</div>;
  }

  return (
    <div className="notification-meta">
      Memo:{' '}
      {tokens.map((token, index) => {
        if (/^\s+$/.test(token)) {
          return token;
        }

        const memoLink = getMemoPostLink(token);
        if (!memoLink) {
          return token;
        }

        return (
          <Link
            key={`${token}-${index}`}
            to={buildPostPath(memoLink.author, memoLink.permlink)}
            className="notification-memo-link"
          >
            {token}
          </Link>
        );
      })}
    </div>
  );
}

function getNotificationFromUsername(notification) {
  return notification?.data?.from || null;
}

function parseVoteWeightPercent(notification) {
  const candidates = [
    notification?.data?.weight,
    notification?.data?.vote_weight,
    notification?.data?.percent,
  ];

  for (const raw of candidates) {
    const num = Number(raw);
    if (!Number.isFinite(num)) continue;
    if (Math.abs(num) > 100) return num / 100;
    return num;
  }

  return null;
}

function parseTransferToken(notification) {
  const amount = String(notification?.data?.amount || '').trim();
  if (!amount) return 'UNKNOWN';
  const parts = amount.split(/\s+/);
  if (parts.length < 2) return 'UNKNOWN';
  const symbol = String(parts[1] || '').trim().toUpperCase();
  return symbol || 'UNKNOWN';
}

function getReplyGroupTarget(notification) {
  const data = notification?.data || {};
  const rootAuthor = data.root_author || data.rootAuthor || data.root_post_author || data.rootPostAuthor || null;
  const rootPermlink = data.root_permlink || data.rootPermlink || data.root_post_permlink || data.rootPostPermlink || null;
  if (rootAuthor && rootPermlink) {
    return {
      author: String(rootAuthor).toLowerCase(),
      permlink: String(rootPermlink),
    };
  }

  const parentAuthor = getReplyParentAuthor(notification);
  const parentPermlink = getReplyParentPermlink(notification);
  if (parentAuthor && parentPermlink) {
    return {
      author: String(parentAuthor).toLowerCase(),
      permlink: String(parentPermlink),
    };
  }

  return null;
}



function getNotificationGroupKey(notification) {
  const type = notification?.type;
  if (!type) return null;

  // votes: group by parent post
  if (type === 'vote' || type === 'downvote') {
    const author = getPostAuthor(notification);
    const permlink = getPostPermlink(notification);
    if (!author || !permlink) return null;
    return `vote:${author}/${permlink}`;
  }

  // replies: group by parent post
  if (isReplyType(type)) {
    const target = getReplyGroupTarget(notification);
    if (!target) return null;
    return `reply:${target.author}/${target.permlink}`;
  }

  // transfers: group by account in token
  if (type === 'transfer') {
    const token = parseTransferToken(notification);
    const from = String(getNotificationFromUsername(notification) || '').toLowerCase();
    if (!from) return null;
    return `transfer:${from}:${token}`;
  }

  // mentions: group by author
  if (type === 'mention') {
    const from = String(getNotificationFromUsername(notification) || '').toLowerCase();
    if (!from) return null;
    return `mention:author:${from}`;
  }

  const author = getPostAuthor(notification);
  const permlink = getPostPermlink(notification);
  if (!author || !permlink) return null;
  return `${type}:${author}/${permlink}`;
}

function isMutedNotification(notification, mutedSet) {
  const from = String(getNotificationFromUsername(notification) || '').toLowerCase();
  if (!from) return false;
  return mutedSet.has(from);
}

function notifyNotificationsChanged(detail = null) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('notifications:changed', { detail }));
  if (detail && Object.prototype.hasOwnProperty.call(detail, 'unreadCount')) {
    window.dispatchEvent(new CustomEvent('notifications:unread-count-changed', { detail }));
  }
}

const Notifications = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const {
    notificationFilterTypes,
    notificationDisplayVoteWeight,
    notiflexionsEnabled,
    notiflexionsGroupByPostAction,
    notiflexionsMutedBehaviorEnabled,
    mutedUsers,
  } = useSettings();
  const { lists } = useLists();
  const navigate = useNavigate();
  const location = useLocation();
  const isHiddenView = location.pathname === '/notifications/hidden';
  const [list, setList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const [openingPostId, setOpeningPostId] = useState(null);
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [showOnlyMuted, setShowOnlyMuted] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [hasHiddenNotifications, setHasHiddenNotifications] = useState(false);
  const [filterTypes, setFilterTypes] = useState(() => (
    Array.isArray(notificationFilterTypes)
      ? notificationFilterTypes
      : getDefaultNotificationFilterTypes()
  ));
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [localNotiflexionReadIds, setLocalNotiflexionReadIds] = useState([]);
  const [localNotiflexionHiddenIds, setLocalNotiflexionHiddenIds] = useState([]);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const selectAllUnreadRef = useRef(null);
  const filterDropdownRef = useRef(null);

  useEffect(() => {
    if (!filterDropdownOpen) return;
    const handleClick = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setFilterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [filterDropdownOpen]);

  useEffect(() => {
    if (!Array.isArray(notificationFilterTypes)) {
      setFilterTypes(getDefaultNotificationFilterTypes());
      return;
    }
    setFilterTypes(notificationFilterTypes);
  }, [notificationFilterTypes]);

  useEffect(() => {
    if (!user?.username) return;
    const readKey = `notiflexions-local-read-${user.username}`;
    const hiddenKey = `notiflexions-local-hidden-${user.username}`;
    try {
      const storedRead = localStorage.getItem(readKey);
      const storedHidden = localStorage.getItem(hiddenKey);
      setLocalNotiflexionReadIds(storedRead ? JSON.parse(storedRead) : []);
      setLocalNotiflexionHiddenIds(storedHidden ? JSON.parse(storedHidden) : []);
    } catch (_err) {
      setLocalNotiflexionReadIds([]);
      setLocalNotiflexionHiddenIds([]);
    }
  }, [user?.username]);

  useEffect(() => {
    if (!user?.username) {
      setTotalUnreadCount(0);
      return;
    }

    let cancelled = false;

    const fetchUnreadCount = async () => {
      try {
        const count = await notificationsApi.unreadCount(user.username);
        if (!cancelled) {
          setTotalUnreadCount(Number(count) || 0);
        }
      } catch (_err) {
        if (!cancelled) {
          setTotalUnreadCount(0);
        }
      }
    };

    fetchUnreadCount();
    const intervalId = setInterval(fetchUnreadCount, 15000);

    const handleNotificationsChanged = () => {
      fetchUnreadCount();
    };

    window.addEventListener('notifications:changed', handleNotificationsChanged);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener('notifications:changed', handleNotificationsChanged);
    };
  }, [user?.username]);

  // List-based notiflexions are now created server-side by the hybrid worker
  // (notifySelectedListAuthorArticle). No synthetic client-side generation needed.

  useEffect(() => {
    if (isHiddenView) return;
    if (!user?.username) return;
    const key = `notifications-show-only-unread-${user.username}`;
    const stored = localStorage.getItem(key);
    setShowOnlyUnread(stored === 'true');
  }, [user?.username, isHiddenView]);

  const loadNotifications = useCallback(async ({ silent = false } = {}) => {
    if (!user?.username) return;
    const headers = getAuthHeaders();
    if (!headers) return;

    if (!silent) {
      setLoading(true);
    }

    const previousScrollY = window.scrollY;

    try {
      const hiddenProbePromise = !isHiddenView
        ? fetch('/api/notifications/hidden?limit=1', { headers })
        : null;

      const endpoint = isHiddenView
        ? `/api/notifications/hidden?limit=${NOTIFICATIONS_PAGE_SIZE}&offset=0`
        : `/api/notifications?username=${encodeURIComponent(user.username)}&limit=${NOTIFICATIONS_PAGE_SIZE}&offset=0&unreadFirst=true`;

      const r = await fetch(endpoint, { headers });
      if (!r.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await r.json();
      const normalized = Array.isArray(data) ? data.slice(0, MAX_DISPLAY_NOTIFICATIONS) : [];

      setList(normalized);

      setSelectedIds((prev) => prev.filter((id) => {
        if (String(id).startsWith('notiflexion-')) return true;
        return normalized.some((n) => n.id === id);
      }));

      if (silent) {
        requestAnimationFrame(() => {
          window.scrollTo({ top: previousScrollY, left: 0, behavior: 'auto' });
        });
      }

      if (hiddenProbePromise) {
        const hiddenProbe = await hiddenProbePromise;
        if (hiddenProbe.ok) {
          const hiddenData = await hiddenProbe.json();
          setHasHiddenNotifications(Array.isArray(hiddenData) && hiddenData.length > 0);
        }
      }
    } catch (err) {
      console.error('load notifications page', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [user?.username, isHiddenView]);

  useEffect(() => {
    if (!user?.username) return;
    loadNotifications({ silent: false });

    const intervalId = setInterval(() => {
      loadNotifications({ silent: true });
    }, 15000);
    return () => clearInterval(intervalId);
  }, [user?.username, loadNotifications, isHiddenView]);

  const mutedSet = useMemo(() => new Set((mutedUsers || []).map((u) => String(u).toLowerCase())), [mutedUsers]);

  const baseNotifications = useMemo(() => {
    return sortUnreadFirstByCreatedAt(
      list.filter((n) => filterTypes.includes(n.type) || (n.type === 'reply_mention' && filterTypes.includes('reply')))
    );
  }, [
    list,
    filterTypes,
  ]);

  const displayedNotifications = useMemo(() => {
    if (!notiflexionsEnabled || !notiflexionsGroupByPostAction || isHiddenView) {
      return baseNotifications;
    }

    const groups = new Map();
    const ordered = [];

    baseNotifications.forEach((notification) => {
      const groupKey = notification?.__notiflexion ? null : getNotificationGroupKey(notification);
      if (!groupKey) {
        ordered.push(notification);
        return;
      }

      const existing = groups.get(groupKey);
      if (!existing) {
        const groupNotification = {
          ...notification,
          id: `notiflexion-group-${groupKey}`,
          __notiflexionGroup: true,
          __groupKey: groupKey,
          __subNotifications: [notification],
          __subUnreadCount: notification.is_read ? 0 : 1,
          is_read: Boolean(notification.is_read),
        };
        groups.set(groupKey, groupNotification);
        ordered.push(groupNotification);
        return;
      }

      existing.__subNotifications.push(notification);
      existing.__subUnreadCount += notification.is_read ? 0 : 1;
      existing.is_read = existing.__subUnreadCount === 0;
    });

    return sortUnreadFirstByCreatedAt(ordered);
  }, [baseNotifications, notiflexionsEnabled, notiflexionsGroupByPostAction, isHiddenView]);

  const notificationsAfterMutedRules = useMemo(() => {
    if (!notiflexionsEnabled || !notiflexionsMutedBehaviorEnabled) {
      return displayedNotifications;
    }

    return displayedNotifications.map((notification) => {
      const checkTarget = notification?.__notiflexionGroup
        ? (notification.__subNotifications || [])[0]
        : notification;
      const muted = isMutedNotification(checkTarget, mutedSet);
      if (muted) {
        return { ...notification, __mutedDisplay: true };
      }
      return notification;
    });
  }, [
    displayedNotifications,
    notiflexionsEnabled,
    notiflexionsMutedBehaviorEnabled,
    mutedSet,
  ]);

  const activeGroupNotification = useMemo(() => {
    if (!activeGroupId) return null;
    return notificationsAfterMutedRules.find((n) => n.id === activeGroupId && n.__notiflexionGroup) || null;
  }, [notificationsAfterMutedRules, activeGroupId]);

  useEffect(() => {
    if (!activeGroupId) return;
    if (!activeGroupNotification) {
      setActiveGroupId(null);
    }
  }, [activeGroupId, activeGroupNotification]);

  // In drill-in (grouped) mode, show all notifications (read and unread)
  const scopedNotifications = useMemo(() => {
    if (!activeGroupNotification) return null;
    return sortUnreadFirstByCreatedAt(activeGroupNotification.__subNotifications || []);
  }, [activeGroupNotification]);

  const displayNotifications = useMemo(() => {
    const source = scopedNotifications || notificationsAfterMutedRules;
    if (!showOnlyMuted) return source;

    return source.filter((notification) => {
      const checkTarget = notification?.__notiflexionGroup
        ? (notification.__subNotifications || [])[0]
        : notification;
      return isMutedNotification(checkTarget, mutedSet);
    });
  }, [notificationsAfterMutedRules, scopedNotifications, showOnlyMuted, mutedSet]);

  const unreadNotifications = useMemo(() => {
    const source = displayNotifications;
    return source.filter((n) => !n.is_read);
  }, [displayNotifications]);
  const readNotifications = useMemo(() => {
    const source = displayNotifications;
    return source.filter((n) => n.is_read);
  }, [displayNotifications]);
  const loadedUnreadNotifications = useMemo(() => {
    return list.filter((n) => !n.is_read);
  }, [list]);
  const loadedNotificationCount = list.length;
  const visibleNotificationCount = displayNotifications.length;
  const unreadIds = useMemo(() => unreadNotifications.map((n) => n.id), [unreadNotifications]);
  const loadedUnreadIds = useMemo(
    () => loadedUnreadNotifications.map((n) => n.id),
    [loadedUnreadNotifications]
  );
  const visibleIds = useMemo(() => displayNotifications.map((n) => n.id), [displayNotifications]);
  const selectedUnreadIds = useMemo(() => selectedIds.filter((id) => unreadIds.includes(id)), [selectedIds, unreadIds]);
  const selectedVisibleIds = useMemo(() => selectedIds.filter((id) => visibleIds.includes(id)), [selectedIds, visibleIds]);
  const allSelectableIds = isHiddenView ? visibleIds : unreadIds;
  const selectedSelectableIds = isHiddenView ? selectedVisibleIds : selectedUnreadIds;
  const allUnreadSelected = allSelectableIds.length > 0 && selectedSelectableIds.length === allSelectableIds.length;

  useEffect(() => {
    if (!selectAllUnreadRef.current) return;
    const partiallySelected = selectedSelectableIds.length > 0 && selectedSelectableIds.length < allSelectableIds.length;
    selectAllUnreadRef.current.indeterminate = partiallySelected;
  }, [selectedSelectableIds.length, allSelectableIds.length]);

  const toggleSelected = (id, checked) => {
    setSelectedIds((prev) => {
      if (checked) {
        if (prev.includes(id)) return prev;
        return [...prev, id];
      }
      return prev.filter((x) => x !== id);
    });
  };

  const toggleSelectAllUnread = (checked) => {
    setSelectedIds(checked ? allSelectableIds : []);
  };

  const toggleAllUnreadSelection = () => {
    setSelectedIds(allUnreadSelected ? [] : allSelectableIds);
  };

  const toggleShowOnlyUnread = () => {
    const next = !showOnlyUnread;
    setShowOnlyUnread(next);
    if (user?.username) {
      localStorage.setItem(`notifications-show-only-unread-${user.username}`, String(next));
    }
  };

  const toggleShowOnlyMuted = () => {
    setShowOnlyMuted((prev) => !prev);
  };

  const expandIdsForGroupedActions = useCallback((ids = []) => {
    const expanded = [];
    ids.forEach((id) => {
      const group = notificationsAfterMutedRules.find((n) => n.id === id && n.__notiflexionGroup);
      if (group) {
        (group.__subNotifications || []).forEach((sub) => {
          if (sub?.id != null) expanded.push(sub.id);
        });
        return;
      }
      expanded.push(id);
    });
    return expanded;
  }, [notificationsAfterMutedRules]);

  const markRead = async ({ ids = [], markAll = false } = {}) => {
    const headers = getAuthHeaders();
    if (!headers) {
      alert('You need to be logged in to mark notifications as read.');
      return;
    }

    if (!markAll && ids.length === 0) return;

    setMarking(true);
    try {
      let targetIdsRaw;
      if (markAll) {
        // If in drill-in mode, only mark children of the current group
        if (activeGroupNotification && scopedNotifications) {
          targetIdsRaw = expandIdsForGroupedActions(scopedNotifications.map((n) => n.id));
        } else {
          targetIdsRaw = expandIdsForGroupedActions(notificationsAfterMutedRules.map((n) => n.id));
        }
      } else {
        targetIdsRaw = expandIdsForGroupedActions(ids);
      }

      const localIds = targetIdsRaw.filter((id) => String(id).startsWith('notiflexion-'));
      const serverIds = targetIdsRaw
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0);

      if (localIds.length > 0 && user?.username) {
        const mergedLocal = Array.from(new Set([...localNotiflexionReadIds, ...localIds]));
        setLocalNotiflexionReadIds(mergedLocal);
        localStorage.setItem(`notiflexions-local-read-${user.username}`, JSON.stringify(mergedLocal));
      }

      const isGlobalMarkAll = markAll && !(activeGroupNotification && scopedNotifications);
      if (serverIds.length > 0 || isGlobalMarkAll) {
        const r = await fetch('/api/notifications/read', {
          method: 'POST',
          headers,
          body: JSON.stringify({ ids: serverIds, markAll: isGlobalMarkAll }),
        });

        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err?.error || 'Failed to mark notifications as read');
        }
      }

      const targetIds = targetIdsRaw;
      const targetUnreadServerIds = targetIds.filter(
        (id) => loadedUnreadIds.includes(id) && !String(id).startsWith('notiflexion-')
      );
      setList((prev) => prev.map((n) => (targetIds.includes(n.id) ? { ...n, is_read: true } : n)));
      if (markAll) {
        setSelectedIds([]);
      } else {
        setSelectedIds((prev) => prev.filter((id) => !targetIds.includes(id)));
      }
      notifyNotificationsChanged({ unreadCount: Math.max(0, totalUnreadCount - targetUnreadServerIds.length) });

      // Broadcast setLastRead to chain:
      // - on any "Mark all read" action (global or group-scoped)
      // - when marking the last unread notification(s) by any method
      const remainingUnreadCount = totalUnreadCount - targetUnreadServerIds.length;
      const shouldBroadcast = markAll || remainingUnreadCount === 0;
      if (shouldBroadcast && user?.username) {
        const nowIso = new Date().toISOString().replace(/\.\d+Z$/, '');
        hiveService.broadcastCustomJson(
          user.username,
          'notify',
          ['setLastRead', { date: nowIso }]
        ).catch((err) => {
          console.warn('[notifications] setLastRead broadcast failed:', err?.message || err);
        });
      }

      // The visible list is already updated optimistically above; unread counts
      // are refreshed through the notifications:changed listener.
    } catch (err) {
      alert(err.message || 'Failed to mark notifications');
    } finally {
      setMarking(false);
    }
  };

  const openNotificationPost = async (notification) => {
    const author = getPostAuthor(notification);
    const permlink = getPostPermlink(notification);
    if (!author || !permlink) return;

    sessionStorage.setItem('returnPath', `${window.location.pathname}${window.location.search}`);

    const targetPath = getPostUrl(notification) || `/post/${author}/${permlink}`;
    navigate(targetPath);
  };

  const openNotificationPostByAuthorPermlink = async (targetAuthor, targetPermlink, notification = null) => {
    if (!targetAuthor || !targetPermlink) return;

    sessionStorage.setItem('returnPath', `${window.location.pathname}${window.location.search}`);

    const routeHints = {
      root_author: notification?.data?.root_author || notification?.data?.rootAuthor || null,
      parent_author: notification?.data?.parent_author || notification?.data?.parentAuthor || null,
    };

    const targetPath = buildContentPath(targetAuthor, targetPermlink, routeHints);
    navigate(targetPath);
  };

  const hideNotifications = async (ids) => {
    const headers = getAuthHeaders();
    if (!headers || !Array.isArray(ids) || ids.length === 0) return;

    const expandedIds = expandIdsForGroupedActions(ids);
    const localIds = expandedIds.filter((id) => String(id).startsWith('notiflexion-'));
    const serverIds = expandedIds.filter((id) => !String(id).startsWith('notiflexion-'));

    if (localIds.length > 0 && user?.username) {
      const nextLocalHidden = Array.from(new Set([...localNotiflexionHiddenIds, ...localIds]));
      setLocalNotiflexionHiddenIds(nextLocalHidden);
      localStorage.setItem(`notiflexions-local-hidden-${user.username}`, JSON.stringify(nextLocalHidden));
      setSelectedIds((prev) => prev.filter((id) => !localIds.includes(id)));
    }

    if (serverIds.length === 0) {
      return;
    }

    try {
      const r = await fetch('/api/notifications/hide', {
        method: 'POST',
        headers,
        body: JSON.stringify({ ids: serverIds }),
      });

      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.error || 'Failed to hide notifications');
      }

      setList((prev) => prev.filter((n) => !expandedIds.includes(n.id)));
      setSelectedIds((prev) => prev.filter((id) => !expandedIds.includes(id)));
      setHasHiddenNotifications(true);
      notifyNotificationsChanged();
    } catch (err) {
      alert(err.message || 'Failed to hide notifications');
    }
  };

  const unhideNotifications = async (ids) => {
    const headers = getAuthHeaders();
    if (!headers || !Array.isArray(ids) || ids.length === 0) return;

    const expandedIds = expandIdsForGroupedActions(ids);
    const localIds = expandedIds.filter((id) => String(id).startsWith('notiflexion-'));
    const serverIds = expandedIds.filter((id) => !String(id).startsWith('notiflexion-'));

    if (localIds.length > 0 && user?.username) {
      const nextLocalHidden = localNotiflexionHiddenIds.filter((id) => !localIds.includes(id));
      setLocalNotiflexionHiddenIds(nextLocalHidden);
      localStorage.setItem(`notiflexions-local-hidden-${user.username}`, JSON.stringify(nextLocalHidden));
      setSelectedIds((prev) => prev.filter((id) => !localIds.includes(id)));
    }

    if (serverIds.length === 0) {
      return;
    }

    try {
      const r = await fetch('/api/notifications/unhide', {
        method: 'POST',
        headers,
        body: JSON.stringify({ ids: serverIds }),
      });

      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err?.error || 'Failed to unhide notifications');
      }

      setList((prev) => {
        const next = prev.filter((n) => !expandedIds.includes(n.id));
        if (isHiddenView) {
          setHasHiddenNotifications(next.length > 0);
        }
        return next;
      });
      setSelectedIds((prev) => prev.filter((id) => !expandedIds.includes(id)));
      notifyNotificationsChanged();
    } catch (err) {
      alert(err.message || 'Failed to unhide notifications');
    }
  };

  // Track show/hide state for muted notifications by id
  const [mutedShowState, setMutedShowState] = useState({});
  const handleShowMuted = useCallback((id, show) => {
    setMutedShowState((prev) => ({ ...prev, [id]: show }));
  }, []);

  const renderNotificationItem = (n, i) => {
    const showMuted = mutedShowState[n.id] || false;
    const primaryNotification = n?.__notiflexionGroup ? (n.__subNotifications?.[0] || n) : n;
    const postUrl = getPostUrl(primaryNotification);
    const txId = getTxId(n);
    const typeLabel = n.type === 'reply_mention' ? 'Reply w. mention' : (TYPE_LABELS[n.type] || n.type);
    const isUnread = !n.is_read;
    const snippet = getNotificationSnippet(primaryNotification);
    const permlinkLabel = isReplyType(n.type)
      ? (getReplyPermlinkLabel(primaryNotification) || getNotificationPermlink(primaryNotification))
      : getNotificationPermlink(primaryNotification);
    const replyParentLabel = isReplyType(n.type) ? getParentPermlinkLabel(primaryNotification) : null;
    const replyParentAuthor = isReplyType(n.type) ? getReplyParentAuthor(primaryNotification) : null;
    const replyParentPermlink = isReplyType(n.type) ? getReplyParentPermlink(primaryNotification) : null;
    const fromUsername = getNotificationFromUsername(primaryNotification);
    const fromUserNode = fromUsername
      ? <Link to={`/user/${fromUsername}`} className="notification-user-link">@{fromUsername}</Link>
      : 'Someone';

    const postAuthor = getPostAuthor(primaryNotification);
    const postPermlink = getPostPermlink(primaryNotification);
    const mentionPostLabel = postAuthor && postPermlink ? `@${postAuthor}/${postPermlink}` : null;
    const canOpenPost = Boolean(postUrl || (n.type === 'mention' && postAuthor && postPermlink));
    const voteWeightPercent = parseVoteWeightPercent(primaryNotification);
    const showVoteWeight = notificationDisplayVoteWeight && (n.type === 'vote' || n.type === 'downvote') && voteWeightPercent !== null;
    const subUnreadCount = n?.__notiflexionGroup ? (n.__subUnreadCount || 0) : 0;
    const subDisplayedCount = subUnreadCount;
    const transferToken = n.type === 'transfer' ? parseTransferToken(primaryNotification) : null;
    const favouriteAuthorList = (n.type === 'favourite_author_post' || n.type === 'favourite_author_comment' || n.type === 'favourite_author_short') && fromUsername
      ? (lists || []).find((listItem) => (
        listItem?.is_favourite && Array.isArray(listItem?.members) && listItem.members.includes(String(fromUsername).toLowerCase())
      ))
      : null;
    const favouriteAuthorListName = favouriteAuthorList?.name || n?.data?.list_name || 'favourites';
    let summaryNode;

    if (n.type === 'notiflexion_list_article') {
      summaryNode = (
        <>
          {fromUserNode} published an article from list <strong>{n?.data?.list_name || 'list'}</strong>
        </>
      );
    } else if (n.__notiflexionGroup && isReplyType(n.type) && replyParentLabel && replyParentAuthor && replyParentPermlink) {
      // Parent reply: show count and parent post permlink
      summaryNode = (
        <>
          {subDisplayedCount} {subDisplayedCount === 1 ? 'reply' : 'replies'} to{' '}
          <button
            type="button"
            className="notification-summary-link"
            onClick={() => openNotificationPostByAuthorPermlink(replyParentAuthor, replyParentPermlink, n)}
            title="Open parent post"
          >
            {replyParentLabel}
          </button>
        </>
      );
    } else if (n.__notiflexionGroup && n.type === 'transfer') {
      // Parent transfer: show user and token symbol
      summaryNode = (
        <>
          {fromUserNode} transferred {transferToken || 'UNKNOWN'}
        </>
      );
    } else if (n.__notiflexionGroup && n.type === 'mention') {
      // Parent mention: show user mentioned you
      summaryNode = (
        <>
          {fromUserNode} mentioned you
        </>
      );
    } else if (n.__notiflexionGroup && (n.type === 'vote' || n.type === 'downvote') && postAuthor && postPermlink) {
      // Parent votes: show count and post permlink
      summaryNode = (
        <>
          {subDisplayedCount} {subDisplayedCount === 1 ? 'vote' : 'vote(s)'} on{' '}
          <button
            type="button"
            className="notification-summary-link"
            onClick={() => openNotificationPostByAuthorPermlink(postAuthor, postPermlink, primaryNotification)}
            title="Open post"
          >
            @{postAuthor}/{postPermlink}
          </button>
        </>
      );
    } else if (isReplyType(n.type) && replyParentLabel && replyParentAuthor && replyParentPermlink) {
      summaryNode = (
        <>
          {fromUserNode} replied{n.type === 'reply_mention' ? ' with a mention' : ''} to{' '}
          <button
            type="button"
            className="notification-summary-link"
            onClick={() => openNotificationPostByAuthorPermlink(replyParentAuthor, replyParentPermlink, n)}
            title="Open parent post"
          >
            {replyParentLabel}
          </button>
        </>
      );
    } else if (n.type === 'mention' && mentionPostLabel) {
      summaryNode = (
        <>
          {fromUserNode} mentioned you in{' '}
          <button
            type="button"
            className="notification-summary-link"
            onClick={() => openNotificationPostByAuthorPermlink(postAuthor, postPermlink, n)}
            title="Open mentioned post"
          >
            a post
          </button>
        </>
      );
    } else if (n.type === 'transfer') {
      summaryNode = (
        <>
          {fromUserNode} sent you {n?.data?.amount || ''}
        </>
      );
    } else if (n.type === 'follow') {
      summaryNode = (
        <>
          {fromUserNode} followed you
        </>
      );
    } else if (n.type === 'favourite_author_post' && postAuthor && postPermlink) {
      summaryNode = (
        <>
          {fromUserNode} from your list <strong>{favouriteAuthorListName}</strong> posted{' '}
          <button
            type="button"
            className="notification-summary-link"
            onClick={() => openNotificationPostByAuthorPermlink(postAuthor, postPermlink, primaryNotification)}
            title="Open flux"
          >
            a flux
          </button>
        </>
      );
    } else if (n.type === 'favourite_author_comment' && postAuthor && postPermlink) {
      summaryNode = (
        <>
          {fromUserNode} from your list <strong>{favouriteAuthorListName}</strong> commented on{' '}
          <button
            type="button"
            className="notification-summary-link"
            onClick={() => openNotificationPostByAuthorPermlink(postAuthor, postPermlink, primaryNotification)}
            title="Open comment"
          >
            a post
          </button>
        </>
      );
    } else if (n.type === 'favourite_author_short' && postAuthor && postPermlink) {
      const feedType = n?.data?.feed_type || 'short';
      const label = feedType === 'sparks' ? 'a spark' : feedType === 'waves' ? 'a wave' : feedType === 'bites' ? 'a bite' : 'a flex';
      summaryNode = (
        <>
          {fromUserNode} from your list <strong>{favouriteAuthorListName}</strong> published{' '}
          <button
            type="button"
            className="notification-summary-link"
            onClick={() => openNotificationPostByAuthorPermlink(postAuthor, postPermlink, primaryNotification)}
            title={`Open ${label}`}
          >
            {label}
          </button>
        </>
      );
    } else if (n.type === 'posting_key_invalid') {
      summaryNode = (
        <span style={{ color: 'var(--danger-color, #dc3545)', fontWeight: 500 }}>
          ⚠️ {n?.data?.message || 'Your Hive posting key is invalid or changed. Please update your key in Settings.'}
        </span>
      );
    } else {
      summaryNode = (
        <>
          {fromUserNode} {TYPE_PAST_TENSE[n.type] || `${(TYPE_LABELS[n.type] || n.type).toLowerCase()}d`} your content
        </>
      );
    }

    // Muted notification content hiding logic
    const isMuted = n.__mutedDisplay;
    return (
      <li key={n.id || i} className={`notification-item ${isUnread ? 'unread' : 'read'}${isMuted ? ' muted-user' : ''}`}>
        <div className="notification-select-col">
          {(isHiddenView || isUnread) ? (
            <input
              type="checkbox"
              checked={selectedIds.includes(n.id)}
              onChange={(e) => toggleSelected(n.id, e.target.checked)}
              disabled={marking}
            />
          ) : (
            <span className="notification-read-dot" title="Already read">✓</span>
          )}
        </div>

        <div className="notification-main-col">
          <div className="notification-title-row">
            <strong>
              {n.type === 'favourite_author_post' ? `Flux from list author` : n.type === 'favourite_author_comment' ? `Comment from list author` : n.type === 'favourite_author_short' ? `Short from list author` : typeLabel}
              {subUnreadCount > 0 ? <span className="notiflexion-subcount"> +{subUnreadCount}</span> : null}
            </strong>
            <span title={formatAbsoluteTime(n.created_at)}>{formatRelativeTime(n.created_at)}</span>
          </div>
          {isMuted && !showMuted ? (
            <div className="notification-muted-content">
              <span>This notification is from a muted user.</span>
              <button className="notif-link-btn btn-secondary" onClick={() => handleShowMuted(n.id, true)}>
                Show
              </button>
            </div>
          ) : (
            <>
              <div className="notification-summary">{summaryNode}</div>
              {n.__notiflexionGroup && subUnreadCount > 0 ? (
                <div className="notification-meta">
                  {subUnreadCount} unread {isReplyType(n.type) ? 'replies' : n.type === 'transfer' ? 'transfers' : n.type === 'mention' ? 'mentions' : 'vote(s)'}
                </div>
              ) : null}
              {!n.__notiflexionGroup && showVoteWeight ? (
                <div className="notification-meta">Weight: {voteWeightPercent.toFixed(2)}%</div>
              ) : null}
              {!n.__notiflexionGroup && n.data?.memo ? renderNotificationMemo(n.data.memo) : null}
              {!n.__notiflexionGroup && snippet ? <div className="notification-snippet">{n.type === 'favourite_author_post' ? snippet : `"${snippet}"`}</div> : null}
              {!n.__notiflexionGroup && permlinkLabel && n.type !== 'mention' ? (
                <button
                  type="button"
                  className="notification-permlink"
                  onClick={() => openNotificationPost(primaryNotification)}
                  title="Open linked post"
                >
                  {permlinkLabel}
                </button>
              ) : null}
              {isMuted && (
                <button className="notif-link-btn btn-secondary" onClick={() => handleShowMuted(n.id, false)}>
                  Hide
                </button>
              )}
            </>
          )}
        </div>
        <div className="notification-actions-col">
          {isMuted && !showMuted ? (
            isUnread && (
              <button
                className="notif-link-btn"
                onClick={() => markRead({ ids: [n.id] })}
                disabled={marking}
              >
                Mark read
              </button>
            )
          ) : (
            <>
              {isUnread && (
                <button
                  className="notif-link-btn"
                  onClick={() => markRead({ ids: [n.id] })}
                  disabled={marking}
                >
                  Mark read
                </button>
              )}
              {n.__notiflexionGroup && !isHiddenView && (
                <button
                  className="notif-link-btn btn-secondary"
                  onClick={() => {
                    setActiveGroupId(n.id);
                    setSelectedIds([]);
                  }}
                >
                  Show activity
                </button>
              )}
              {canOpenPost && (
                <button
                  className="notif-link-btn"
                  onClick={() => { openNotificationPost(primaryNotification); markRead({ ids: [n.id] }); }}
                  disabled={openingPostId === (n.id || `${getPostAuthor(primaryNotification)}/${getPostPermlink(primaryNotification)}`)}
                  title="Will also mark as read"
                >
                  {openingPostId === (n.id || `${getPostAuthor(primaryNotification)}/${getPostPermlink(primaryNotification)}`) ? 'Opening...' : 'View post'}
                </button>
              )}
              {!isHiddenView && !isUnread && (
                <button
                  className="notif-link-btn"
                  onClick={() => hideNotifications([n.id])}
                >
                  Hide
                </button>
              )}
              {isHiddenView && (
                <button
                  className="notif-link-btn"
                  onClick={() => unhideNotifications([n.id])}
                >
                  Unhide
                </button>
              )}
              {txId && (
                <a
                  href={`https://hivexplorer.com/tx/${txId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="notif-link-btn"
                >
                  TX
                </a>
              )}
            </>
          )}
        </div>
      </li>
    );
  };

  if (authLoading) {
    return (
      <>
        <SidebarHeader title="Notifications" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <div className="notifications-page">
          <div className="notifications-state">Loading notifications...</div>
        </div>
      </>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <SidebarHeader title="Notifications" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <div className="notifications-page">
        <div className="notifications-toolbar">
          <div className="notifications-toolbar-left">
            <label className="notifications-select-all">
              <input
                ref={selectAllUnreadRef}
                type="checkbox"
                checked={allUnreadSelected}
                onChange={(e) => toggleSelectAllUnread(e.target.checked)}
                disabled={allSelectableIds.length === 0 || marking}
              />
              <span>{isHiddenView ? 'Select all hidden' : (filterTypes.length < ALL_NOTIFICATION_TYPES.length ? 'Select all visible unread' : 'Select all unread')}</span>
            </label>
            <button
              className="notif-btn notif-btn-secondary"
              onClick={toggleAllUnreadSelection}
              disabled={allSelectableIds.length === 0 || marking}
            >
              {allUnreadSelected
                ? (isHiddenView ? 'Deselect all hidden' : (filterTypes.length < ALL_NOTIFICATION_TYPES.length ? 'Deselect all visible unread' : 'Deselect all unread'))
                : (isHiddenView ? 'Select all hidden' : (filterTypes.length < ALL_NOTIFICATION_TYPES.length ? 'Select all visible unread' : 'Select all unread'))}
            </button>
            <span className="notifications-count">
              {isHiddenView
                ? `Hidden: ${visibleIds.length}`
                : `Unread: ${totalUnreadCount}`
              }
            </span>
            {!isHiddenView && totalUnreadCount > MAX_DISPLAY_NOTIFICATIONS && (
              <span className="notifications-count">
                Showing first {MAX_DISPLAY_NOTIFICATIONS} unread notifications.
              </span>
            )}
            {!isHiddenView && notiflexionsEnabled && notiflexionsGroupByPostAction && (
              <span className="notifications-count">Grouped view is enabled, so many unread items may appear as fewer grouped rows.</span>
            )}
            {!isHiddenView && !activeGroupNotification && (
              <div className="notifications-unread-toggle-wrap">
                <span className="notifications-unread-toggle-label">Show unread only</span>
                <button
                  type="button"
                  className={`notifications-unread-toggle ${showOnlyUnread ? 'right' : 'left'}`}
                  onClick={toggleShowOnlyUnread}
                  aria-label="Toggle show unread only"
                >
                  <span className={`notifications-unread-toggle-option ${!showOnlyUnread ? 'active' : ''}`}>Off</span>
                  <span className={`notifications-unread-toggle-option ${showOnlyUnread ? 'active' : ''}`}>On</span>
                  <span className="notifications-unread-toggle-slider" />
                </button>
              </div>
            )}
            {activeGroupNotification && (
              <div className="notifications-group-drill-header">
                <button
                  type="button"
                  className="notif-btn notif-btn-secondary"
                  onClick={() => {
                    setActiveGroupId(null);
                    setSelectedIds([]);
                  }}
                >
                  Back to all groups
                </button>
                <span className="notifications-group-drill-label">Showing grouped activity only</span>
              </div>
            )}
          </div>
          <div className="notifications-toolbar-right">
            {!isHiddenView && (
              <>
                <button
                  className="notif-btn"
                  onClick={() => markRead({ ids: selectedUnreadIds })}
                  disabled={selectedUnreadIds.length === 0 || marking}
                >
                  {marking ? 'Marking...' : `Mark read${selectedUnreadIds.length > 0 ? ` (${selectedUnreadIds.length})` : ''}`}
                </button>
                {activeGroupNotification && scopedNotifications ? (
                  <button
                    className="notif-btn notif-btn-secondary"
                    onClick={() => markRead({ ids: scopedNotifications.filter((n) => !n.is_read).map((n) => n.id) })}
                    disabled={scopedNotifications.filter((n) => !n.is_read).length === 0 || marking}
                  >
                    Mark all read
                  </button>
                ) : (
                  <button
                    className="notif-btn notif-btn-secondary"
                    onClick={() => markRead({ markAll: true })}
                    disabled={unreadIds.length === 0 || marking}
                  >
                    Mark all read
                  </button>
                )}
              </>
            )}
            {isHiddenView && (
              <button
                className="notif-btn"
                onClick={() => unhideNotifications(selectedVisibleIds)}
                disabled={selectedVisibleIds.length === 0 || marking}
              >
                Unhide selected
              </button>
            )}
            {!isHiddenView && !activeGroupNotification && (
              <div className="notif-filter-wrap" ref={filterDropdownRef}>
                <button
                  type="button"
                  className={`notif-btn notif-btn-secondary notif-filter-btn${filterTypes.length < ALL_NOTIFICATION_TYPES.length ? ' notif-filter-active' : ''}`}
                  onClick={() => setFilterDropdownOpen((o) => !o)}
                  aria-expanded={filterDropdownOpen}
                >
                  Filter{filterTypes.length < ALL_NOTIFICATION_TYPES.length ? ` (${filterTypes.length}/${ALL_NOTIFICATION_TYPES.length})` : ''}
                  <span className="notif-filter-caret" aria-hidden="true">▾</span>
                </button>
                {filterDropdownOpen && (
                  <div className="notif-filter-dropdown" role="menu">
                    <button
                      type="button"
                      className="notif-filter-toggle-all"
                      onClick={() =>
                        setFilterTypes(
                          filterTypes.length === ALL_NOTIFICATION_TYPES.length
                            ? []
                            : ALL_NOTIFICATION_TYPES
                        )
                      }
                    >
                      {filterTypes.length === ALL_NOTIFICATION_TYPES.length ? 'Deselect all' : 'Select all'}
                    </button>
                    {Object.entries(NOTIFICATION_TYPE_LABELS).map(([type, label]) => (
                      <label key={type} className="notif-filter-item">
                        <input
                          type="checkbox"
                          checked={filterTypes.includes(type)}
                          onChange={(e) => {
                            setFilterTypes((prev) =>
                              e.target.checked ? [...prev, type] : prev.filter((t) => t !== type)
                            );
                          }}
                        />
                        {label}
                      </label>
                    ))}
                    <label className="notif-filter-item">
                      <input
                        type="checkbox"
                        checked={showOnlyMuted}
                        onChange={toggleShowOnlyMuted}
                      />
                      Only muted users
                    </label>
                  </div>
                )}
              </div>
            )}
            {(isHiddenView || hasHiddenNotifications) && (
              <Link className="notif-btn notif-btn-secondary" to={isHiddenView ? '/notifications' : '/notifications/hidden'}>
                {isHiddenView ? 'View notifications' : 'View hidden'}
              </Link>
            )}
          </div>
        </div>

        {loading && (displayNotifications.length === 0 && list.length === 0) && <div className="notifications-state">Loading notifications...</div>}
        {!loading && activeGroupNotification && displayNotifications.length === 0 && <div className="notifications-state">No notifications in this group</div>}
        {!loading && !activeGroupNotification && displayNotifications.length === 0 && <div className="notifications-state">No notifications found</div>}

        {(
          (activeGroupNotification && displayNotifications.length > 0) ||
          (!activeGroupNotification && displayNotifications.length > 0)
        ) && !isHiddenView && (
          <ul className="notifications-list">
            {activeGroupNotification
              ? displayNotifications.map(renderNotificationItem)
              : [
                  ...unreadNotifications.map(renderNotificationItem),
                  !showOnlyUnread && unreadNotifications.length > 0 && readNotifications.length > 0 && (
                    <li className="notifications-separator" aria-hidden="true" key="read-separator">
                      <span>Read notifications</span>
                    </li>
                  ),
                  !showOnlyUnread && readNotifications.map(renderNotificationItem),
                ].flat().filter(Boolean)
            }
          </ul>
        )}

        {displayNotifications.length > 0 && isHiddenView && (
          <ul className="notifications-list">
            {displayNotifications.map(renderNotificationItem)}
          </ul>
        )}

      </div>
    </>
  );
};

export default Notifications;